<template>
	<VSwitch
		:id="id"
		ref="inputSwitch"
		v-model="inputValue"
		:aria-describedby="describedby"
		:aria-required="ariaRequired"
		class="amelipro-switch"
		:class="labelLeft ? 'amelipro-switch--left' : ''"
		:disabled="disabled"
		:hide-details="hideErrorMessage"
		:readonly="readonly"
		:ripple="false"
		:rules="inputRules"
		:style="globalFieldStyles"
		@change="emitChangeEvent"
	>
		<template #label>
			{{ label }}

			<span v-if="ariaRequired">
				<span aria-hidden="true">
					&nbsp;*
				</span>

				<span class="d-sr-only">
					&nbsp;Champ obligatoire
				</span>
			</span>
		</template>

		<template #message="{ message }">
			<AmeliproMessage
				:id="errorId"
				no-icon
				text
				type="error"
			>
				<p class="mb-0">
					{{ message }}
				</p>
			</AmeliproMessage>
		</template>
	</VSwitch>
</template>

<script setup lang="ts">
	import { PropType, computed, ref, watch } from 'vue';
	import AmeliproMessage from '../AmeliproMessage';
	import { IInputSwitch } from './types';
	import { IndexedObject } from '../../types';
	import { VSwitch } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		descriptionId: {
			type: String,
			default: undefined,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		globalMaxWidth: {
			type: String,
			default: undefined,
		},
		globalMinWidth: {
			type: String,
			default: undefined,
		},
		globalWidth: {
			type: String,
			default: undefined,
		},
		hideErrorMessage: {
			type: [String, Boolean] as PropType<string | boolean>,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		label: {
			type: String,
			required: true,
		},
		labelLeft: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		rules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		value: {
			type: Boolean,
			default: false,
		},
	});

	const inputSwitch = ref<IInputSwitch>();
	const valid = ref(true);
	const inputValue = ref(false);

	watch(() => props.value, (newValue: boolean | undefined) => {
		inputValue.value = Boolean(newValue);
	}, { immediate: true });

	const inputRules = computed<ValidationRule[]>(() => {
		// const { rules } = props; => provoque des mutations indésirables de props.rules
		const rules = [
			...props.rules,
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		return rules;
	});

	const errorId = computed<string>(() => `${props.id}-error`);

	const describedby = computed<string | undefined>(() => {
		if (props.descriptionId !== undefined) {
			return valid.value ? props.descriptionId : `${props.descriptionId} ${errorId.value}`;
		}
		return valid.value ? undefined : errorId.value;
	});

	const globalFieldStyles = computed<IndexedObject>(() => ({
		maxWidth: props.globalMaxWidth,
		minWidth: props.globalMinWidth,
		width: props.globalWidth,
	}));

	const emit = defineEmits(['change']);
	const emitChangeEvent = (): void => {
		emit('change', inputValue.value);
		valid.value = Boolean(inputSwitch.value?.validate());
	};
</script>

<style lang="scss" scoped>
.amelipro-switch {
	& :deep(.v-label) {
		font-size: $font-size-xs !important;
		color: $ap-grey-darken1 !important;
	}

	&.v-input--selection-controls {
		margin-top: 0;
		padding-top: 0;
	}

	&.amelipro-switch--left {
		display: inline-flex;

		& :deep(.v-input__slot) {
			flex-direction: row-reverse;

			& .v-input--selection-controls__input {
				margin-right: 0;
				margin-left: 8px;
			}
		}
	}

	& :deep(.v-input--selection-controls__input) {
		height: 2rem;
	}

	& :deep(.v-input--switch__track) {
		background-color: $ap-grey;
		border-radius: 1.5rem;
	}

	& :deep(.v-input--switch__thumb) {
		background-color: $ap-white;
		left: 0.25rem;
	}

	&.v-input--is-label-active {

		& :deep(.v-input--switch__track) {
			background-color: $ap-blue-darken1;
		}
	}

	&.v-input--is-disabled {

		& :deep(.v-input--switch__track) {
			opacity: 0.6;
		}
	}

	& :deep(.v-label--is-disabled),
	& :deep(.v-label.error--text) {
		color: $ap-grey !important;
	}

	&.v-input--is-focused {

		& :deep(.v-input--selection-controls__input) {
			outline: 1px dotted $ap-grey;
		}
	}

	&.v-input--is-dirty {
		& :deep(.v-input--switch__thumb) {
			transform: translate(1.625rem, 0) !important;
		}
	}
}
</style>
